import React, { useState, useEffect, useMemo } from "react";
import { unstable_batchedUpdates } from "react-dom";

import "react-toastify/dist/ReactToastify.css";
import "./styles/dark-atmosphere.css";
import { QueryClient, QueryClientProvider } from "react-query";

import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery, CssBaseline } from "@material-ui/core";
import ImpersonationBanner from "./components/ImpersonationBanner";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";

import { SocketContext, socketManager } from './context/Socket/SocketContext';
import useSettings from "./hooks/useSettings";
// react-favicon is used only by NotificationsPopOver (which manages the
// badge count). The import was removed here on 2026-05-30 together with
// the duplicate plain <Favicon> that caused a second (black) icon to appear.
import { getBackendURL } from "./services/config";
import { heIL, enUS, esES, ruRU } from '@material-ui/core/locale';


import Routes from "./routes";
import { i18n } from "./translate/i18n";
import { I18nextProvider } from "react-i18next";

// Map 2-letter i18n code → MUI locale bundle + writing direction.
// Keep in sync with the language options exposed in Settings/Options.js
// and with the translation files under translate/languages/.
// Note: MUI v4 ships no Arabic locale (`arSA`/`arEG` were added in v5),
// so we fall back to `enUS` for date-picker / pagination strings while
// keeping `dir: "rtl"` so the rest of the UI flips correctly.
const LANGUAGE_MAP = {
  he: { mui: heIL, dir: "rtl" },
  ar: { mui: enUS, dir: "rtl" },
  en: { mui: enUS, dir: "ltr" },
  es: { mui: esES, dir: "ltr" },
  ru: { mui: ruRU, dir: "ltr" },
};

// Exported so the auth flow can purge it on tenant change. Without the
// purge, react-query keeps the previous tenant's tickets/contacts in
// memory and serves them to the next tenant before the first refetch
// completes — Danylo flagged 2026-05-15 seeing GoDeal Tourism tickets
// in another tenant's notification dropdown after account switching.
export const queryClient = new QueryClient();
const defaultLogoLight = "/vector/logo.svg";
const defaultLogoDark = "/vector/logo-dark.svg";
const defaultLogoFavicon = "/vector/favicon.svg";

const App = () => {
  const [locale, setLocale] = useState();
  const [direction, setDirection] = useState("rtl"); // Default RTL for Hebrew


  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState("#25d366");
  const [primaryColorDark, setPrimaryColorDark] = useState("#2ee37a");
  const [appLogoLight, setAppLogoLight] = useState("");
  const [appLogoDark, setAppLogoDark] = useState("");
  const [appLogoFavicon, setAppLogoFavicon] = useState("");
  const [appName, setAppName] = useState("");
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const next = prevMode === "light" ? "dark" : "light";
          // Persist ONLY on an explicit toggle — this is what lets the app
          // follow the OS theme until the user chooses to override it.
          window.localStorage.setItem("preferredTheme", next);
          return next;
        });
      },
      setPrimaryColorLight: (color) => {
        setPrimaryColorLight(color);
      },
      setPrimaryColorDark: (color) => {
        setPrimaryColorDark(color);
      },
      setAppLogoLight: (file) => {
        setAppLogoLight(file);
      },
      setAppLogoDark: (file) => {
        setAppLogoDark(file);
      },
      setAppLogoFavicon: (file) => {
        setAppLogoFavicon(file);
      },
      setAppName: (name) => {
        setAppName(name);
      }
    }),
    []
  );

  const calculatedLogoDark = () => {
    if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
      return appLogoLight;
    }
    return appLogoDark;
  };
  const calculatedLogoLight = () => {
    if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
      return appLogoDark;
    }
    return appLogoLight;
  };

  const theme = useMemo(() => createTheme(
    {
      scrollbarStyles: {
        // Contain scroll chaining so flicking past a list edge doesn't
        // rubber-band the whole page; WebkitOverflowScrolling is a legacy
        // iOS momentum-scroll fallback. Every primary scroll surface
        // (messages list, tickets list, contacts, chat dialogs, etc.)
        // pulls this in via theme.scrollbarStyles.
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        "&::-webkit-scrollbar": {
          width: '8px',
          height: '8px',
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: mode === "light" ? "#f1f5f9" : "#131614",
          borderRadius: '8px',
        },
        "&::-webkit-scrollbar-thumb": {
          boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
          backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
          borderRadius: '8px',
        },
      },
      scrollbarStylesSoft: {
        "&::-webkit-scrollbar": {
          width: "8px",
          borderRadius: '20px',
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: mode === "light" ? "#e2e2e52b" : "#333333",
          boxShadow:'inset 0 0 3px rgba(0, 0, 0, 0.3)',
          scrollbarWidth: 'thin',
          borderRadius: '20px',
        },
      },
      palette: {
        type: mode,
        primary: { main: mode === "light" ? primaryColorLight : primaryColorDark },
        background: {
          default: mode === "light" ? "#fafafa" : "#0B0D0C",
          paper: mode === "light" ? "#ffffff" : "#131614",
        },
        textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
        borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
        dark: { main: mode === "light" ? "#333333" : "#666" },
        light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
        tabHeaderBackground: mode === "light" ? "#EEE" : "#131614",
        optionsBackground: mode === "light" ? "#fafafa" : "#131614",
        options: mode === "light" ? "#fafafa" : "#666",
        fontecor: mode === "light" ? primaryColorLight : primaryColorDark,
        fancyBackground: mode === "light" ? "#fafafa" : "#131614",
        bordabox: mode === "light" ? "#eee" : "#131614",
        newmessagebox: mode === "light" ? "#eee" : "#131614",
        inputdigita: mode === "light" ? "#fff" : "#666",
        contactdrawer: mode === "light" ? "#fff" : "#666",
        announcements: mode === "light" ? "#ededed" : "#131614",
        login: mode === "light" ? "#fff" : "#1C1C1C",
        announcementspopover: mode === "light" ? "#fff" : "#131614",
        chatlist: mode === "light" ? "#eee" : "#131614",
        boxlist: mode === "light" ? "#ededed" : "#131614",
        boxchatlist: mode === "light" ? "#ededed" : "#131614",
        total: mode === "light" ? "#fff" : "#222",
        messageIcons: mode === "light" ? "grey" : "#F3F3F3",
        inputBackground: mode === "light" ? "#FFFFFF" : "#131614",
        barraSuperior: mode === "light" ? primaryColorLight : "#666",
        boxticket: mode === "light" ? "#EEE" : "#131614",
        campaigntab: mode === "light" ? "#ededed" : "#131614",
      },
      mode,
      direction,
      overrides: {
        // Android Chromium WebView (esp. Samsung Internet) auto-inflates
        // text when it thinks a column is too narrow, which breaks our
        // fixed-height layouts (Chat container, MUI Tabs, input bar) on
        // small viewports — root cause #1 of the Android "shrinkage"
        // reports. Lock text-size-adjust to 100% on html/body so the
        // browser respects our explicit font sizes.
        MuiCssBaseline: {
          "@global": {
            "html, body": {
              textSizeAdjust: "100%",
              WebkitTextSizeAdjust: "100%",
            },
          },
        },
        MuiSvgIcon: {
          root: {
            color: mode === "light" ? '#000000' : "#ffffff"
          }
        },
        // Brand-aligned LinearProgress. The default looked like a
        // raw grey/black bar in dark mode (Raviv flagged it as ugly).
        // We give the track a soft brand-tinted surface, the moving
        // bar a brand-green gradient + glow + rounded corners. Same
        // treatment for both indeterminate and determinate variants.
        MuiLinearProgress: {
          root: {
            height: 6,
            borderRadius: 999,
            overflow: "hidden",
            backgroundColor:
              mode === "light"
                ? "rgba(37, 211, 102, 0.10)"
                : "rgba(46, 227, 122, 0.10)",
          },
          colorPrimary: {
            backgroundColor:
              mode === "light"
                ? "rgba(37, 211, 102, 0.10)"
                : "rgba(46, 227, 122, 0.10)",
          },
          bar: {
            borderRadius: 999,
            background:
              "linear-gradient(90deg, #25d366 0%, #128c7e 100%)",
            boxShadow:
              mode === "light"
                ? "0 0 8px rgba(37, 211, 102, 0.45)"
                : "0 0 10px rgba(46, 227, 122, 0.55)",
          },
          barColorPrimary: {
            background:
              "linear-gradient(90deg, #25d366 0%, #128c7e 100%)",
          },
        },
        // Modern Menu Styling
        MuiMenu: {
          paper: {
            borderRadius: 12,
            marginTop: 8,
            minWidth: 180,
            // Elevated surface in dark: one notch up from cards
            // (#1A1E1B vs #131614) + inset top highlight + a real
            // drop shadow because popovers DO float above content.
            boxShadow: mode === "light"
              ? '0 10px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)'
              : 'inset 0 1px 0 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(255,255,255,0.06), 0 8px 16px -4px rgba(0,0,0,0.5), 0 24px 48px -12px rgba(0,0,0,0.7)',
            backgroundColor: mode === "light" ? '#ffffff' : '#1A1E1B',
            border: mode === "light" ? 'none' : '1px solid rgba(255,255,255,0.07)',
          },
          list: {
            padding: '8px',
            backgroundColor: mode === "light" ? '#ffffff' : '#1A1E1B',
          },
        },
        // Select Dropdown Styling — RTL fix for the arrow icon. MUI v4
        // hard-codes `right: 7px` on `.MuiSelect-icon`; without
        // jss-rtl (we don't ship it) the chevron stays glued to the
        // right edge even when the document direction is RTL. That
        // produced the "label and arrow both on the right" look in
        // every Settings dropdown. Now flips to the leading edge
        // based on the active theme.direction.
        MuiSelect: {
          select: {
            '&:focus': {
              backgroundColor: 'transparent',
            },
            // Reserve space for the chevron on the correct side so
            // the value text never bleeds under it.
            paddingRight: direction === "rtl" ? 14 : 28,
            paddingLeft: direction === "rtl" ? 28 : 14,
          },
          icon: direction === "rtl"
            ? { left: 7, right: 'auto' }
            : { right: 7, left: 'auto' },
        },
        MuiInputBase: {
          root: {
            backgroundColor: 'transparent',
          },
          input: {
            backgroundColor: 'transparent',
          },
        },
        // Premium dark Paper recipe — surface sits at #131614 (≈
        // 6 lightness pts above bg #0B0D0C) with a 1px hairline
        // border and the Linear-signature inset top highlight
        // `inset 0 1px 0 rgba(255,255,255,0.04)`. That single
        // line is the difference between "color-swapped dark"
        // and premium dark.
        MuiPaper: {
          root: {
            backgroundColor: mode === "light" ? '#ffffff' : '#131614',
            border: mode === "light" ? '1px solid #eef0f5' : '1px solid rgba(255,255,255,0.07)',
            boxShadow: mode === "light"
              ? 'none'
              : 'inset 0 1px 0 0 rgba(255,255,255,0.04)',
          },
          rounded: {
            borderRadius: 14,
          },
          elevation1: {
            boxShadow: mode === "light"
              ? '0 4px 18px rgba(15, 23, 42, 0.04)'
              : 'inset 0 1px 0 0 rgba(255,255,255,0.04)',
          },
        },
        // Premium table look — applies to every existing CRUD page
        // (Tags, Users, Contacts, Queues, Connections, Schedules…)
        // without touching them individually.
        MuiTableHead: {
          root: {
            backgroundColor: mode === "light" ? '#f8fafc' : 'rgba(255,255,255,0.02)',
          },
        },
        MuiTableCell: {
          head: {
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: mode === "light" ? '#64748b' : '#a8acb3',
            borderBottom: mode === "light"
              ? '1px solid #eef0f5'
              : '1px solid rgba(255,255,255,0.07)',
            padding: '14px 18px',
          },
          body: {
            fontSize: 13,
            color: mode === "light" ? '#0B0D0C' : '#f4f5f6',
            borderBottom: mode === "light"
              ? '1px solid #f4f4f5'
              : '1px solid rgba(255,255,255,0.05)',
            padding: '14px 18px',
          },
        },
        MuiTableRow: {
          root: {
            transition: 'background-color .15s ease',
            '&:hover': {
              backgroundColor: mode === "light"
                ? 'rgba(37, 211, 102, 0.06)'
                : 'rgba(46, 227, 122, 0.06)',
            },
          },
          head: {
            '&:hover': { backgroundColor: 'transparent' },
          },
        },
        // Inputs in dark — surface-3 well (#1F2421), hairline border
        // that brightens on focus to brand glow. Brand-color outline
        // on focus is the premium pattern (Linear, Vercel both do it).
        MuiOutlinedInput: {
          root: {
            borderRadius: 10,
            backgroundColor: mode === "light" ? 'transparent' : '#1F2421',
            '& $notchedOutline': {
              borderColor: mode === "light" ? '#e5e7eb' : 'rgba(255,255,255,0.08)',
            },
            '&:hover $notchedOutline': {
              borderColor: mode === "light" ? '#a1a1aa' : 'rgba(255,255,255,0.18)',
            },
            '&.Mui-focused $notchedOutline': {
              borderColor: mode === "light" ? '#25d366' : '#2ee37a',
              borderWidth: '1px',
              boxShadow: mode === "light"
                ? '0 0 0 3px rgba(37, 211, 102, 0.15)'
                : '0 0 0 3px rgba(46, 227, 122, 0.18)',
            },
          },
          notchedOutline: {
            borderColor: mode === "light" ? '#e5e7eb' : 'rgba(255,255,255,0.08)',
            transition: 'border-color .15s ease, box-shadow .15s ease',
          },
        },
        MuiMenuItem: {
          root: {
            borderRadius: 8,
            margin: '2px 0',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 500,
            transition: 'all 0.15s ease',
            color: mode === "light" ? '#374151' : '#e2e8f0',
            '&:hover': {
              backgroundColor: mode === "light"
                ? 'rgba(37, 211, 102, 0.10)'
                : 'rgba(37, 211, 102, 0.18)',
            },
            '&.Mui-selected': {
              backgroundColor: mode === "light"
                ? 'rgba(37, 211, 102, 0.14)'
                : 'rgba(37, 211, 102, 0.28)',
              '&:hover': {
                backgroundColor: mode === "light"
                  ? 'rgba(37, 211, 102, 0.20)'
                  : 'rgba(37, 211, 102, 0.34)',
              },
            },
          },
        },
        // Modern Popover Styling
        MuiPopover: {
          paper: {
            borderRadius: 12,
            boxShadow: mode === "light"
              ? '0 10px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)'
              : 'inset 0 1px 0 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(255,255,255,0.06), 0 8px 16px -4px rgba(0,0,0,0.5), 0 24px 48px -12px rgba(0,0,0,0.7)',
            backgroundColor: mode === "light" ? '#ffffff' : '#1A1E1B',
            border: mode === "light" ? 'none' : '1px solid rgba(255,255,255,0.07)',
          },
        },
        // Modern Dialog/Modal Styling
        MuiDialog: {
          paper: {
            borderRadius: 16,
            boxShadow: mode === "light"
              ? '0 25px 50px rgba(0,0,0,0.15)'
              : 'inset 0 1px 0 0 rgba(255,255,255,0.06), 0 25px 50px rgba(0,0,0,0.55)',
            backgroundColor: mode === "light" ? '#ffffff' : '#1A1E1B',
            border: mode === "light" ? 'none' : '1px solid rgba(255,255,255,0.07)',
          },
        },
        MuiDialogTitle: {
          root: {
            fontSize: '18px',
            fontWeight: 600,
            padding: '20px 24px 12px',
          },
        },
        MuiDialogContent: {
          root: {
            padding: '12px 24px 20px',
          },
        },
        MuiDialogActions: {
          root: {
            padding: '12px 24px 20px',
          },
        },
        // Force the Tabs indicator + selected-tab text to brand
        // green everywhere. Without this, MUI uses palette.primary
        // (configurable per-tenant), but several pages override
        // Tabs `indicatorColor="primary"` with hard-coded blue or
        // inherit the old #39ACE7 default and end up with a blue
        // underline on Settings / AI Workforce / AI Dashboard.
        // This override wins because it has both attr selectors
        // and !important on the things that matter.
        MuiTabs: {
          indicator: {
            backgroundColor: mode === "light" ? "#25d366" : "#2ee37a",
            height: 3,
            borderRadius: 3,
          },
        },
        MuiTab: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13.5,
            letterSpacing: -0.1,
            color: mode === "light" ? "#71717a" : "rgba(255,255,255,0.55)",
            transition: "color .15s ease",
            "&:hover": {
              color: mode === "light" ? "#0a0a0a" : "#f4f5f6",
            },
            "&.Mui-selected": {
              color: mode === "light" ? "#128c7e" : "#86efac",
              fontWeight: 700,
            },
          },
        },
        // Modern Button Styling
        MuiButton: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(37, 211, 102, 0.45)',
            },
          },
          containedPrimary: {
            background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
            color: '#fff',
            '&:hover': {
              background: 'linear-gradient(135deg, #1ebe5d 0%, #075e54 100%)',
            },
          },
          // MUI v4 hard-codes physical margin-right/margin-left on
          // .MuiButton-startIcon / .MuiButton-endIcon and we don't ship
          // jss-rtl, so under Hebrew (direction: rtl) the icons collide
          // with / overlap the label (Save Settings, Connect/Disconnect,
          // New Chat, etc.). Flip to logical-equivalent spacing keyed to
          // the active theme.direction so the icon always sits on the
          // leading edge of the text with a real 8px gap.
          startIcon: {
            marginLeft: direction === "rtl" ? 8 : 0,
            marginRight: direction === "rtl" ? 0 : 8,
            display: "inline-flex",
            alignItems: "center",
          },
          endIcon: {
            marginLeft: direction === "rtl" ? 0 : 8,
            marginRight: direction === "rtl" ? 8 : 0,
            display: "inline-flex",
            alignItems: "center",
          },
        },
        // Modern Chip Styling
        MuiChip: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
        MuiListItemText: {
          multiline: {
            direction: direction || "ltr",
            textAlign: direction === "rtl" ? "right" : "left",
            marginBottom: 0
          },
        },
        MuiInputBase: {
          root: {
            direction: direction || "ltr",
            textAlign: direction === "rtl" ? "right" : "left",
          },
        },
        MuiTypography: {
          root: {
            direction: direction || "ltr",
            textAlign: direction === "rtl" ? "right" : "left",
          },
        },
      },
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      calculatedLogoLight,
      calculatedLogoDark,
      calculatedLogo: () => {
        if (mode === "light") {
          return calculatedLogoLight();
        }
        return calculatedLogoDark();
      }
    },
    locale
  ), [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight, direction]);

  useEffect(() => {
    // i18n.js seeds `lng: localStorage.getItem('language') || 'he'`, so by
    // the time this effect runs i18n.language already reflects the user's
    // saved pick. We mirror that pick onto MUI locale + writing direction.
    // Default to Hebrew (the Whapiz baseline) when nothing is saved yet.
    const lang = (localStorage.getItem("language") || "he").slice(0, 2);
    const entry = LANGUAGE_MAP[lang] || LANGUAGE_MAP.he;

    i18n.changeLanguage(lang).catch(() => { /* fall through to current i18n state */ });
    setLocale(entry.mui);
    setDirection(entry.dir);
  }, []);

  useEffect(() => {
    // Mirror the active theme to a data attribute so the global
    // dark-atmosphere stylesheet can layer body-level effects
    // (atmospheric top glow, SVG noise overlay, brand focus ring,
    // custom scrollbar) without coupling to the MUI class names.
    if (typeof document !== "undefined") {
      document.body.setAttribute("data-theme", mode);
      // Paint the page AND the native shell to match the theme. The
      // html/body background is what shows through the status-bar and
      // home-indicator safe areas in the mobile app - leaving it white
      // produced white strips at the top/bottom in dark mode.
      const shellBg = mode === "dark" ? "#0F1210" : "#ffffff";
      document.documentElement.style.backgroundColor = shellBg;
      document.body.style.backgroundColor = shellBg;
      import("@capacitor/core")
        .then(({ Capacitor }) => {
          if (!Capacitor || !Capacitor.isNativePlatform()) return null;
          return import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
            StatusBar.setStyle({
              style: mode === "dark" ? Style.Dark : Style.Light,
            }).catch(() => {});
            // Android only - iOS rejects, the catch swallows it.
            return StatusBar.setBackgroundColor({ color: shellBg }).catch(() => {});
          });
        })
        .catch(() => {});
    }
  }, [mode]);

  useEffect(() => {
    // Follow the OS light/dark setting live, unless the user explicitly
    // picked a theme (toggleColorMode stores preferredTheme).
    if (!window.localStorage.getItem("preferredTheme")) {
      setMode(prefersDarkMode ? "dark" : "light");
    }
  }, [prefersDarkMode]);

  useEffect(() => {
    // Mirror direction onto the html element so the bidi-fix CSS
    // selectors (html[dir="rtl"] …) can hit. MUI sets `direction`
    // via JSS but never writes the dir attribute, so without this
    // English/Russian strings rendered inside the RTL container
    // get their trailing punctuation flipped to the left side
    // (".Search" instead of "Search...").
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("dir", direction || "ltr");
    }
  }, [direction]);

  useEffect(() => {
    // Primary color defaults — used by MUI's primary palette which
    // drives Tabs indicator, .Mui-selected, contained-primary
    // buttons, and many other "brand-coloured" surfaces. The old
    // defaults of #0000FF / #39ACE7 leaked as blue tab underlines
    // on Settings / AI Workforce / AI Dashboard and as blue numbers
    // on stat tiles, completely off-brand. Switched to WhatsApp
    // green so any page that hasn't been individually re-themed
    // still ends up brand-aligned by default.
    // One batched commit instead of six independent .then setters: React 17
    // does NOT batch promise callbacks, so each resolved setting invalidated
    // the createTheme useMemo and re-rendered the whole tree (4-6 full theme
    // rebuilds during boot). Per-key semantics preserved exactly: a rejected
    // key logs and leaves its state at the initial value (appName keeps its
    // explicit "whitelabel chat" rejection fallback).
    Promise.allSettled([
      getPublicSetting("primaryColorLight"),
      getPublicSetting("primaryColorDark"),
      getPublicSetting("appLogoLight"),
      getPublicSetting("appLogoDark"),
      getPublicSetting("appLogoFavicon"),
      getPublicSetting("appName"),
    ]).then(([pcl, pcd, logoL, logoD, favicon, appNameR]) => {
      unstable_batchedUpdates(() => {
        if (pcl.status === "fulfilled") setPrimaryColorLight(pcl.value || "#25d366");
        else console.log("Error reading setting", pcl.reason);
        if (pcd.status === "fulfilled") setPrimaryColorDark(pcd.value || "#2ee37a");
        else console.log("Error reading setting", pcd.reason);
        if (logoL.status === "fulfilled") setAppLogoLight(logoL.value ? (`${getBackendURL()}/public/${logoL.value}`) : defaultLogoLight);
        else console.log("Error reading setting", logoL.reason);
        if (logoD.status === "fulfilled") setAppLogoDark(logoD.value ? (`${getBackendURL()}/public/${logoD.value}`) : defaultLogoDark);
        else console.log("Error reading setting", logoD.reason);
        if (favicon.status === "fulfilled") setAppLogoFavicon(favicon.value ? (`${getBackendURL()}/public/${favicon.value}`) : null);
        else console.log("Error reading setting", favicon.reason);
        if (appNameR.status === "fulfilled") setAppName(appNameR.value || "godeal");
        else { console.log("Error reading setting", appNameR.reason); setAppName("whitelabel chat"); }
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
    {/* Favicon with badge count is managed exclusively by NotificationsPopOver
        to avoid duplicate <link rel="icon"> elements. The plain Favicon here
        was causing a second (black/unbadged) icon to appear alongside the red
        badged one. Removed 2026-05-30. */}
    <ColorModeContext.Provider value={{ colorMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ImpersonationBanner />
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <SocketContext.Provider value={socketManager}>
              <ActiveMenuProvider>

                <Routes key={i18n.language} />
              </ActiveMenuProvider>
            </SocketContext.Provider>
          </I18nextProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
    </>
  );
};

export default App;
